import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Sparkles, DoorOpen, Key } from 'lucide-react';

// Ensure React is available
const ReactMemo = React?.memo || ((component: any) => component);

const containerClass =
  'container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8';

const QuickStartGuide = ReactMemo(() => {
  return (
    <section
      className="border-y border-neutral-100 bg-gradient-to-b from-neutral-50/80 to-white py-20 sm:py-24"
      aria-labelledby="quickstart-heading"
      role="region"
    >
      <div className={containerClass}>
        <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-700">
            Get started
          </p>
          <h2
            id="quickstart-heading"
            className="text-balance text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl md:text-[2.25rem] md:leading-snug"
          >
            From first message to keys in hand
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            Whether you want a curated shortlist or prefer to explore listings yourself, here is the path most renters follow on RentaBahay.
          </p>
        </header>

        <div
          className="mx-auto grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-10"
          role="list"
          aria-label="Getting started steps"
        >
          <div className="text-center" role="listitem">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 shadow-sm ring-1 ring-primary-100 md:h-16 md:w-16"
              aria-hidden="true"
            >
              <ClipboardList className="h-7 w-7 md:h-8 md:w-8" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-neutral-900 md:text-lg">
              1. Share your brief
            </h3>
            <p className="text-sm leading-relaxed text-neutral-600 md:text-[0.9375rem]">
              Use the &quot;Find me a home&quot; form with your area, budget, property type, and move-in timing.
            </p>
          </div>

          <div className="text-center" role="listitem">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 shadow-sm ring-1 ring-primary-100 md:h-16 md:w-16"
              aria-hidden="true"
            >
              <Sparkles className="h-7 w-7 md:h-8 md:w-8" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-neutral-900 md:text-lg">
              2. Get curated matches
            </h3>
            <p className="text-sm leading-relaxed text-neutral-600 md:text-[0.9375rem]">
              A specialist shortlists rentals that fit your life—not an endless scroll of maybes.
            </p>
          </div>

          <div className="text-center" role="listitem">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 shadow-sm ring-1 ring-primary-100 md:h-16 md:w-16"
              aria-hidden="true"
            >
              <DoorOpen className="h-7 w-7 md:h-8 md:w-8" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-neutral-900 md:text-lg">
              3. Tour what fits
            </h3>
            <p className="text-sm leading-relaxed text-neutral-600 md:text-[0.9375rem]">
              Book viewings, compare options, and ask questions before you commit.
            </p>
          </div>

          <div className="text-center" role="listitem">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 shadow-sm ring-1 ring-primary-100 md:h-16 md:w-16"
              aria-hidden="true"
            >
              <Key className="h-7 w-7 md:h-8 md:w-8" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-neutral-900 md:text-lg">
              4. Apply and move in
            </h3>
            <p className="text-sm leading-relaxed text-neutral-600 md:text-[0.9375rem]">
              Get help with next steps—from paperwork pointers to staying in touch with your agent or landlord.
            </p>
          </div>
        </div>

        <p className="mx-auto mt-12 max-w-2xl text-center text-sm leading-relaxed text-neutral-600 sm:mt-14">
          <span className="text-neutral-700">Prefer to browse on your own?</span>{' '}
          <Link
            to="/search"
            className="font-semibold text-primary-600 underline decoration-2 decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-700 hover:decoration-primary-500"
          >
            Search all listings
          </Link>
        </p>
      </div>
    </section>
  );
});

QuickStartGuide.displayName = 'QuickStartGuide';

export default QuickStartGuide;
